import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Typography } from '@mui/material';

import cleanEmptyValues from '@/util/cleanEmptyValues';
import GuardrailServiceConfig from './GuardrailServiceConfig';
import ContentSafetySwitch from './ContentSafetySwitch';
import JailbreakDetectionSwitch from './JailbreakDetectionSwitch';
import InjectionDetectionSwitch from './InjectionDetectionSwitch';

export default function EndpointGuardrail({
  initialValues,
  onChange,
  edit = false,
}) {
  const [protected_service, setProtectedService] = useState(
    initialValues?.protected_service || {},
  );
  const [content_safety, setContentSafety] = useState(
    Boolean(initialValues?.content_safety),
  );
  const [content_safety_service, setContentSafetyService] = useState(
    initialValues?.content_safety_service || {},
  );
  const [jailbreak_detection, setJailbreakDetection] = useState(
    Boolean(initialValues?.jailbreak_detection),
  );
  const [jailbreak_detection_service, setJailbreakDetectionService] = useState(
    initialValues?.jailbreak_detection_service || {},
  );
  const [injection_detection, setInjectionDetection] = useState(
    Boolean(initialValues?.injection_detection),
  );

  const isInitialMount = useRef(true);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const protected_service_error = !protected_service?.id;
  const content_safety_service_error =
    content_safety && !content_safety_service?.id;
  const has_error = protected_service_error || content_safety_service_error;

  const payload = useMemo(() => {
    const guardrail = {
      protected_service,
      content_safety,
      jailbreak_detection,
      injection_detection,
    };

    if (content_safety) {
      guardrail.content_safety_service = content_safety_service;
    }
    if (jailbreak_detection) {
      const { api_key, ...service_without_api_key } =
        jailbreak_detection_service;
      guardrail.jailbreak_detection_service = service_without_api_key;
    }

    return cleanEmptyValues(guardrail) || {};
  }, [
    protected_service,
    content_safety,
    content_safety_service,
    jailbreak_detection,
    jailbreak_detection_service,
    injection_detection,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      onChangeRef.current(payload, has_error);
      return;
    }
    onChangeRef.current(payload, has_error);
  }, [payload, has_error]);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='body1' fontWeight='medium' sx={{ mb: 1 }}>
          Guardrail Configuration
        </Typography>
      </Grid>

      <Grid size={12}>
        <GuardrailServiceConfig
          title='Protected Service'
          value={protected_service}
          onChange={setProtectedService}
          disabled={edit}
          required
          error={protected_service_error}
          service_label='Protected Service'
          label_id='guardrail-protected-service-label'
        />
      </Grid>

      <Grid size={12}>
        <ContentSafetySwitch
          value={content_safety}
          onChange={setContentSafety}
          disabled={edit}
        />
      </Grid>

      {content_safety ? (
        <Grid size={12}>
          <GuardrailServiceConfig
            title='Content Safety Service'
            value={content_safety_service}
            onChange={setContentSafetyService}
            disabled={edit}
            required
            error={content_safety_service_error}
            service_label='Content Safety Service'
            label_id='guardrail-content-safety-service-label'
          />
        </Grid>
      ) : null}

      <Grid size={12}>
        <JailbreakDetectionSwitch
          value={jailbreak_detection}
          onChange={setJailbreakDetection}
          disabled={edit}
        />
      </Grid>

      {jailbreak_detection ? (
        <Grid size={12}>
          <GuardrailServiceConfig
            title='Jailbreak Detection Service'
            value={jailbreak_detection_service}
            onChange={setJailbreakDetectionService}
            disabled={edit}
            service_label='Jailbreak Detection Service'
            label_id='guardrail-jailbreak-detection-service-label'
            show_api_key={false}
          />
        </Grid>
      ) : null}

      <Grid size={12}>
        <InjectionDetectionSwitch
          value={injection_detection}
          onChange={setInjectionDetection}
          disabled={edit}
        />
      </Grid>
    </Grid>
  );
}
